import { RefObject, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ErrorToast } from '../adminComponents/Toasts/Toasts';
import { reportError } from '../helpers/reportError';

/**
 * Cloudflare Turnstile, wrapped so a call site only ever awaits a token.
 *
 * The widget is a third-party iframe that has to escape every stacking context
 * and outlive the modal subtree it was triggered from, so this hook owns its
 * DOM imperatively instead of returning something to render. That is
 * deliberate: if a call site had to remember to render a slot, forgetting it on
 * one of the six flows would silently disable the captcha there, and TypeScript
 * could not catch it.
 *
 * @see https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
 */

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  // Force the light (white) widget and English copy rather than letting
  // Turnstile follow the visitor's theme / browser language.
  theme?: 'light' | 'dark' | 'auto';
  language?: string;
  // 'flexible' makes the widget span its container's width (like the inputs).
  size?: 'normal' | 'flexible' | 'compact';
  retry?: 'auto' | 'never';
  callback?: (token: string) => void;
  'error-callback'?: (code?: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  'before-interactive-callback'?: () => void;
};

export type TurnstileApi = {
  /** Returns the widget id, or `undefined` if the widget could not be created. */
  render: (
    element: HTMLElement,
    options: TurnstileRenderOptions
  ) => string | undefined;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
};

// Must live inside this module rather than a standalone `.d.ts`: partner,
// brandOwner and sales all have `include: ["src/**/*"]` in tsconfig.app.json, so
// an ambient file under components/src would never be picked up by them. A
// module reached through the `@vgrubs/components` barrel is pulled into all
// four programs. Being a module, it also *must* be wrapped in `declare global`
// — a bare `interface Window` here would be module-local and do nothing.
declare global {
  interface Window {
    turnstile?: TurnstileApi;
    onVgrubsTurnstileLoad?: () => void;
  }
}

/** Values are sent to Cloudflare as-is; must match `^[a-zA-Z0-9_-]{1,32}$`. */
export type CaptchaAction =
  | 'admin-sign-in'
  | 'partner-sign-in'
  | 'agent-sign-in'
  | 'partner-withdraw'
  | 'admin-balance'
  /* Opening the support chat on a tracking page - the backend checks this
     action name (docs/dispatcher-chat-front.md). */
  | 'dispatcherChat'
  | 'games-otp';

/**
 * Sign-in is one shared page compiled into all four SPAs, but each app has its
 * own Turnstile widget. Derived from NX_APP_TYPE the same way `isAdmin` and
 * friends are, and read at call time rather than module scope so this file
 * stays free of imports from the helpers barrel.
 *
 * brandOwner shares the partner sitekey, so it shares the partner action too —
 * the backend may validate `action` when it calls siteverify.
 */
export const signInCaptchaAction = (): CaptchaAction => {
  switch (process.env.NX_APP_TYPE) {
    case 'admin':
      return 'admin-sign-in';
    case 'sales':
      return 'agent-sign-in';
    default:
      return 'partner-sign-in';
  }
};

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onVgrubsTurnstileLoad';
const SCRIPT_TIMEOUT_MS = 15000;
const OVERLAY_Z_INDEX = 2147483000;

let scriptPromise: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    let settled = false;

    const fail = (reason: string) => {
      if (settled) return;
      settled = true;
      // Drop the cached promise so a later attempt can retry. Caching the
      // rejection would strand anyone who hit this behind an ad blocker or a
      // dropped connection until they did a full page reload.
      scriptPromise = null;
      reject(new Error(reason));
    };

    const timer = setTimeout(
      () => fail('Turnstile script timed out'),
      SCRIPT_TIMEOUT_MS
    );

    window.onVgrubsTurnstileLoad = () => {
      if (settled) return;
      clearTimeout(timer);
      // The script can fire onload without populating the global; that is a
      // load failure, not a success with an undefined API.
      if (!window.turnstile) return fail('Turnstile loaded without an API');
      settled = true;
      resolve(window.turnstile);
    };

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      clearTimeout(timer);
      fail('Turnstile script blocked or unreachable');
    };

    document.head.appendChild(script);
  });

  return scriptPromise;
};

const failQuietly = (message: string, userMessage: string) => {
  // Log + Sentry get the diagnostic string; the user gets plain language.
  reportError(new Error(message), { context: 'useCaptcha', silent: true });
  ErrorToast(userMessage);
};

/**
 * Returns `getCaptchaToken`, which resolves a fresh single-use Turnstile token,
 * or `null` if the challenge failed or the user cancelled (an error toast has
 * already been shown in that case).
 *
 * It never rejects — several call sites invoke it from a floating promise, so a
 * rejection would surface as an unhandled rejection.
 *
 * @example
 * const { getCaptchaToken } = useCaptcha(
 *   process.env.NX_TURNSTILE_SITE_KEY_WITHDRAW,
 *   'partner-withdraw'
 * );
 *
 * const token = await getCaptchaToken();
 * if (!token) return;
 * await withdraw({ amount, captcha: { token } });
 */
/**
 * Inline host for the Turnstile widget, meant to sit right above a form's
 * submit button. Pass its ref as the third argument to `useCaptcha` to opt a
 * form into inline rendering; drop both the ref and this slot to fall back to
 * the full-screen overlay.
 *
 * Spacing only kicks in once a challenge is actually visible: the hook sets a
 * `data-captcha-active` attribute at that point (Turnstile injects an invisible
 * element even when no interaction is needed, so a CSS `:empty` check can't tell
 * the two apart and would reserve an empty gap). Keeping the margin here in CSS
 * makes it easy to tune the look per design.
 *
 * It sits above the app's loading spinner (z-index 200) on purpose, so a form
 * that keeps a spinner running still shows the widget over it when a challenge
 * needs the user.
 */
export const CaptchaSlot = styled.div<{ $activeMargin?: string }>`
  position: relative;
  z-index: 300;
  /* Block so the 'flexible' widget can stretch to the full container width. */
  display: block;
  width: 100%;

  /* Spacing differs per placement (e.g. login's button already has a top
     margin, modals don't), so callers can override via $activeMargin. */
  &[data-captcha-active] {
    margin: ${({ $activeMargin }) => $activeMargin ?? '12px 0'};
  }
`;

export const useCaptcha = (
  siteKey: string | undefined,
  action: CaptchaAction,
  // Optional inline host. When provided, the challenge renders into this
  // element (e.g. a slot placed right above a form's submit button) instead of
  // the full-screen overlay — no dimming, no Cancel. Omit it and the hook falls
  // back to the managed overlay, so a call site can never accidentally end up
  // with no captcha at all.
  containerRef?: RefObject<HTMLElement | null>,
  // Fired with `true` when a challenge actually opens and needs the user, and
  // `false` once it resolves. Lets a call site drop its button spinner while
  // the user is solving the captcha, then resume it.
  onInteractive?: (isInteractive: boolean) => void
): { getCaptchaToken: () => Promise<string | null> } => {
  const inline = !!containerRef;

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  // Refs, not state: a second click in the same tick has to see the in-flight
  // request, and a state update would not have landed by then.
  const settleRef = useRef<((token: string | null) => void) | null>(null);

  useEffect(() => {
    // Warm the script now so the first challenge does not also pay for the
    // network fetch. Failures are swallowed here and re-surfaced by
    // getCaptchaToken, which is where the user is actually waiting on it.
    loadTurnstile().catch(() => undefined);

    // Inline mode: the caller owns the host element, so there is no overlay to
    // build or tear down.
    if (inline) {
      return () => {
        settleRef.current?.(null);
      };
    }

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      zIndex: String(OVERLAY_Z_INDEX),
      // Transparent and click-through until a challenge actually needs the
      // user. If `before-interactive-callback` never fires but the widget shows
      // itself anyway, it is still centred and clickable — just undimmed.
      background: 'transparent',
      pointerEvents: 'none',
    });

    const host = document.createElement('div');
    host.style.pointerEvents = 'auto';
    overlay.appendChild(host);

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Cancel';
    Object.assign(cancel.style, {
      display: 'none',
      pointerEvents: 'auto',
      cursor: 'pointer',
      border: 'none',
      borderRadius: '200px',
      padding: '10px 32px',
      font: 'inherit',
      fontSize: '14px',
      background: '#ffffff',
      color: '#1a1a1a',
    });
    const onCancel = () => settleRef.current?.(null);
    cancel.addEventListener('click', onCancel);
    overlay.appendChild(cancel);

    document.body.appendChild(overlay);
    overlayRef.current = overlay;
    hostRef.current = host;
    cancelRef.current = cancel;

    return () => {
      settleRef.current?.(null);
      cancel.removeEventListener('click', onCancel);
      overlay.remove();
      overlayRef.current = null;
      hostRef.current = null;
      cancelRef.current = null;
    };
  }, [inline]);

  const getCaptchaToken = useCallback(async (): Promise<string | null> => {
    // Supersede whatever is in flight (e.g. Owner then Manager on the partner
    // login) instead of stacking a second widget into the same host.
    settleRef.current?.(null);

    if (!siteKey) {
      failQuietly(
        `Missing Turnstile site key for action "${action}"`,
        'Verification is unavailable right now. Please try again later.'
      );
      return null;
    }

    let turnstile: TurnstileApi;
    try {
      turnstile = await loadTurnstile();
    } catch (error) {
      reportError(error, { context: 'useCaptcha', silent: true });
      ErrorToast(
        'Could not load verification. Check your connection or ad blocker and try again.'
      );
      return null;
    }

    // Inline renders into the caller's slot; overlay/cancel exist only in
    // overlay mode.
    const host = inline ? containerRef?.current ?? null : hostRef.current;
    const overlay = overlayRef.current;
    const cancel = cancelRef.current;
    // Unmounted while the script was loading, or an inline slot that never
    // rendered.
    if (!host) return null;

    return new Promise<string | null>((resolve) => {
      let done = false;

      const settle = (token: string | null) => {
        if (done) return;
        done = true;
        settleRef.current = null;

        if (overlay) {
          overlay.style.background = 'transparent';
          overlay.style.pointerEvents = 'none';
        }
        if (cancel) cancel.style.display = 'none';
        // Collapse the inline slot's spacing again now the widget is gone.
        if (inline) host.removeAttribute('data-captcha-active');
        onInteractive?.(false);

        const widgetId = widgetIdRef.current;
        widgetIdRef.current = null;
        if (widgetId) removeWidget(turnstile, widgetId);

        resolve(token);
      };

      settleRef.current = settle;

      const widgetId = turnstile.render(host, {
        sitekey: siteKey,
        action,
        appearance: 'interaction-only',
        theme: 'light',
        language: 'en',
        size: 'flexible',
        // Default is 'auto', which retries a failed challenge indefinitely —
        // error-callback would never fire and this promise would hang.
        retry: 'never',
        callback: settle,
        // Fires only when the user actually has to interact — i.e. when the
        // widget becomes visible. Inline, add the slot's spacing now (so an
        // invisible/passive render never reserves a gap); overlay mode dims the
        // backdrop and offers a Cancel.
        'before-interactive-callback': () => {
          onInteractive?.(true);
          if (inline) {
            host.setAttribute('data-captcha-active', 'true');
            return;
          }
          if (overlay) {
            overlay.style.background = 'rgba(0, 0, 0, 0.55)';
            overlay.style.pointerEvents = 'auto';
          }
          if (cancel) cancel.style.display = 'block';
        },
        'error-callback': (code) => {
          failQuietly(
            `Turnstile challenge failed${code ? ` (${code})` : ''}`,
            'Verification failed. Please try again.'
          );
          settle(null);
        },
        'timeout-callback': () => {
          failQuietly(
            'Turnstile challenge timed out',
            'Verification timed out. Please try again.'
          );
          settle(null);
        },
      });

      if (!widgetId) {
        failQuietly(
          `Turnstile widget could not be created for action "${action}"`,
          'Verification is unavailable right now. Please try again later.'
        );
        settle(null);
        return;
      }

      // A cached clearance can invoke `callback` during render, before this
      // line — in which case settle() already ran with a null widget id and the
      // widget we just created has to be cleaned up here instead.
      if (done) {
        removeWidget(turnstile, widgetId);
        return;
      }

      widgetIdRef.current = widgetId;
    });
  }, [siteKey, action, inline, onInteractive]);

  return { getCaptchaToken };
};

/**
 * Removing the iframe synchronously from inside the widget's own callback
 * throws inside Turnstile's message handler, so always defer a tick.
 */
const removeWidget = (turnstile: TurnstileApi, widgetId: string) => {
  setTimeout(() => {
    try {
      turnstile.remove(widgetId);
    } catch {
      // Already torn down — nothing to do.
    }
  }, 0);
};
